import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Download, Loader2 } from "lucide-react";
import { showError, showSuccess } from "@/utils/toast";

interface ExportDialogProps {
    isOpen: boolean;
    onClose: () => void;
    gameId: number;
    versionId: number;
}

export const ExportDialog = ({ isOpen, onClose, gameId, versionId }: ExportDialogProps) => {
    const [status, setStatus] = useState<"idle" | "initiating" | "processing" | "completed" | "error">("idle");
    const [progress, setProgress] = useState(0);
    const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [selectedExportType, setSelectedExportType] = useState<"accepted" | "eng_hun" | "hun_suggested" | "eng_hun_suggested" | "eng_name">("accepted");

    const csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content;

    const checkExportStatus = (exportId: number) => {
        const pollInterval = setInterval(async () => {
            try {
                const response = await fetch(`/api/games/${gameId}/versions/${versionId}/bulk_exports/${exportId}`, {
                    headers: {
                        "X-CSRF-Token": csrfToken || "",
                    },
                });

                const data = await response.json();

                if (!response.ok) {
                    clearInterval(pollInterval);
                    setErrorMessage("Failed to check export status");
                    setStatus("error");
                    showError("Failed to check export status");
                    return;
                }

                if (data.status === "completed") {
                    clearInterval(pollInterval);
                    setProgress(100);
                    setDownloadUrl(data.download_url);
                    setStatus("completed");
                    showSuccess("Export completed! Ready to download.");
                } else if (data.status === "failed") {
                    clearInterval(pollInterval);
                    setErrorMessage("Export failed. Please try again.");
                    setStatus("error");
                    showError("Export failed. Please try again.");
                } else if (data.status === "processing") {
                    // Still processing, update progress
                    setProgress((prev) => Math.min(95, prev + Math.random() * 20));
                }
            } catch (error) {
                clearInterval(pollInterval);
                const message = error instanceof Error ? error.message : "Unknown error";
                setErrorMessage(message);
                setStatus("error");
                showError(message);
            }
        }, 2000); // Poll every 2 seconds
    };

    const startExport = async (exportType: "accepted" | "eng_hun" | "hun_suggested" | "eng_hun_suggested" | "eng_name" = "accepted") => {
        setStatus("initiating");
        setProgress(0);
        setDownloadUrl(null);
        setErrorMessage(null);

        try {
            const response = await fetch(`/api/games/${gameId}/versions/${versionId}/bulk_exports`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": csrfToken || "",
                },
                body: JSON.stringify({ export_type: exportType }),
            });

            const data = await response.json();

            if (!response.ok) {
                if (data.error === "An export is already in progress for this version") {
                    // Resume existing export
                    setStatus("processing");
                    setProgress(50);
                    checkExportStatus(data.bulk_export_id);
                } else {
                    setErrorMessage(data.error || "Failed to start export");
                    setStatus("error");
                    showError(data.error || "Failed to start export");
                }
            } else {
                setStatus("processing");
                setProgress(10);
                // Start polling for status
                checkExportStatus(data.id);
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : "Unknown error";
            setErrorMessage(message);
            setStatus("error");
            showError(message);
        }
    };

    const handleDownload = () => {
        if (downloadUrl) {
            window.location.href = downloadUrl;
            onClose();
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Fordítások exportálása</DialogTitle>
                    <DialogDescription>
                        Az összes elfogadott magyar fordítás letöltése ZIP fájlba.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {status === "idle" && (
                        <>
                            <p className="text-sm text-gray-600">
                                Válasszon exportálási típust:
                            </p>
                            <div className="space-y-2">
                                <Button onClick={() => { setSelectedExportType("accepted"); startExport("accepted"); }} className="w-full">
                                    Elfogadott (HU csak)
                                </Button>
                                <Button onClick={() => { setSelectedExportType("eng_hun"); startExport("eng_hun"); }} className="w-full" variant="outline">
                                    Eng + Magyar
                                </Button>
                                <Button onClick={() => { setSelectedExportType("hun_suggested"); startExport("hun_suggested"); }} className="w-full" variant="outline">
                                    Magyar + Ajánlottak
                                </Button>
                                <Button onClick={() => { setSelectedExportType("eng_hun_suggested"); startExport("eng_hun_suggested"); }} className="w-full" variant="outline">
                                    Eng + Magyar + Ajánlottak
                                </Button>
                                <Button onClick={() => { setSelectedExportType("eng_name"); startExport("eng_name"); }} className="w-full" variant="outline">
                                    Angol(fájlnév)
                                </Button>
                            </div>
                        </>
                    )}

                    {status === "initiating" && (
                        <div className="flex items-center justify-center space-x-2 py-4">
                            <Loader2 className="h-5 w-5 animate-spin" />
                            <span className="text-sm">Exportálás előkészítése...</span>
                        </div>
                    )}

                    {status === "processing" && (
                        <>
                            <div className="space-y-2">
                                <div className="flex items-center justify-between text-sm">
                                    <span>Exportálás folyamatban...</span>
                                    <span className="text-xs text-gray-500">{Math.round(progress)}%</span>
                                </div>
                                <Progress value={progress} className="h-2" />
                            </div>
                            <p className="text-xs text-gray-500 text-center">
                                A feldolgozás hosszabb ideig tarthat a fájlok és fordítások számától függően.
                            </p>
                        </>
                    )}

                    {status === "completed" && (
                        <>
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <p className="text-sm text-green-800 font-medium">✓ Az exportálás sikeresen befejeződött!</p>
                            </div>
                            <Button onClick={handleDownload} className="w-full flex items-center justify-center space-x-2">
                                <Download className="h-4 w-4" />
                                <span>ZIP letöltése</span>
                            </Button>
                        </>
                    )}

                    {status === "error" && (
                        <>
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <p className="text-sm text-red-800 font-medium">✕ Az exportálás sikertelen</p>
                                {errorMessage && <p className="text-xs text-red-700 mt-1">{errorMessage}</p>}
                            </div>
                            <Button onClick={() => startExport(selectedExportType)} className="w-full" variant="outline">
                                Újra próbálkozás
                            </Button>
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};
